import React from 'react';
import { InlineSpinner } from '../../common/loading';

import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  FormHelperText,
  Button,
  Autocomplete,
  Chip,
alpha,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption } from '../../../services/api';
import { FormData } from './types';
import { getProjectStatusColor, filterProjectsByStatus } from './utils';
import {
  parseRecipientTokens,
  isValidEmailAddress,
  normalizeEmail,
  splitEmailList,
  joinEmailList,
} from './emailRecipients';

interface EmailRecipientsChipFieldProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  helperText: string;
  sx: SxProps<Theme>;
}

/** Ref handle to merge pending typed text into chips before submit/validation. */
export type EmailRecipientsChipFieldHandle = {
  flushPendingInput: () => void;
};

const EmailRecipientsChipField = React.forwardRef<
  EmailRecipientsChipFieldHandle,
  EmailRecipientsChipFieldProps
>(function EmailRecipientsChipField(
  { label, required, value, onChange, disabled, helperText, sx },
  ref
) {
  const emails = React.useMemo(() => splitEmailList(value), [value]);
  const [inputValue, setInputValue] = React.useState('');
  const [rejections, setRejections] = React.useState<string[]>([]);

  const commit = React.useCallback(
    (nextList: string[]) => {
      const valid: string[] = [];
      const invalid: string[] = [];
      for (const e of nextList) {
        const n = normalizeEmail(e);
        if (n && isValidEmailAddress(n)) valid.push(n);
        else if (e.trim()) invalid.push(e.trim());
      }
      setRejections(invalid);
      onChange(joinEmailList(valid));
    },
    [onChange]
  );

  const expandTokens = React.useCallback((tokens: string[]) => {
    const expanded: string[] = [];
    for (const t of tokens) {
      const parsed = parseRecipientTokens(t);
      if (parsed.length > 0) {
        expanded.push(...parsed);
      } else if (t.trim()) {
        expanded.push(t.trim());
      }
    }
    return expanded;
  }, []);

  /** Merge pending input into chips. `rawOverride` uses the DOM value on blur (authoritative). */
  const flushPendingInput = React.useCallback(
    (rawOverride?: string) => {
      const source = rawOverride !== undefined ? rawOverride : inputValue;
      const raw = source.trim();
      if (!raw) {
        setInputValue('');
        return;
      }
      setInputValue('');
      const parsed = parseRecipientTokens(raw);
      if (parsed.length > 0) {
        commit([...emails, ...parsed]);
      } else {
        commit([...emails, raw]);
      }
    },
    [inputValue, emails, commit]
  );

  React.useImperativeHandle(
    ref,
    () => ({
      flushPendingInput: () => flushPendingInput(),
    }),
    [flushPendingInput]
  );

  const handleAutocompleteChange = (_: unknown, newValue: string[]) => {
    commit(expandTokens(newValue));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text/plain');
    const parsed = parseRecipientTokens(raw);
    if (parsed.length === 0 && raw.trim()) {
      setRejections([raw.trim().length > 100 ? `${raw.trim().slice(0, 100)}…` : raw.trim()]);
      setInputValue('');
      return;
    }
    commit([...emails, ...parsed]);
    setInputValue('');
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    flushPendingInput(e.target.value);
  };

  const showError = rejections.length > 0;
  const combinedHelper = showError
    ? `Invalid or could not parse: ${rejections.join('; ')} · ${helperText}`
    : helperText;

  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={[]}
      value={emails}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset') setInputValue('');
        else setInputValue(next);
      }}
      onChange={handleAutocompleteChange}
      filterOptions={(opts) => opts}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={`${option}-${index}`}
            label={option}
            size="small"
            sx={{
              borderRadius: 1,
              maxWidth: '100%',
              '& .MuiChip-label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          /* Native `required` on the inner input fails when value lives in chips, not the input. */
          required={false}
          InputLabelProps={{ shrink: true, required }}
          placeholder={
            emails.length === 0
              ? 'Type an email, press Enter to add — or paste many addresses at once'
              : ''
          }
          helperText={combinedHelper}
          error={showError}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            onPaste: (ev: React.ClipboardEvent<HTMLInputElement>) => {
              handlePaste(ev);
            },
          }}
          inputProps={{
            ...params.inputProps,
            required: false,
            onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
              params.inputProps.onBlur?.(ev);
              handleInputBlur(ev);
            },
          }}
        />
      )}
    />
  );
});

interface FeedbackFormProps {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | number | null | File) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  projectOptions: ProjectDropdownOption[];
  projectsLoading: boolean;
  projectStatusFilter: string | null;
  onProjectStatusFilterChange: (filter: string | null) => void;
  selectedProject: ProjectDropdownOption | null;
  onProjectSelect: (project: ProjectDropdownOption | null) => void;
  isFormDisabled: boolean;
}

export type FeedbackFormHandle = {
  flushRecipientInputs: () => void;
};

export const FeedbackForm = React.forwardRef<FeedbackFormHandle, FeedbackFormProps>(
  function FeedbackForm(
    {
      formData,
      onInputChange,
      onFileChange,
      projectOptions,
      projectsLoading,
      projectStatusFilter,
      onProjectStatusFilterChange,
      selectedProject,
      onProjectSelect,
      isFormDisabled,
    },
    ref
  ) {
  const toEmailsFieldRef = React.useRef<EmailRecipientsChipFieldHandle>(null);
  const ccEmailsFieldRef = React.useRef<EmailRecipientsChipFieldHandle>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      flushRecipientInputs: () => {
        toEmailsFieldRef.current?.flushPendingInput();
        ccEmailsFieldRef.current?.flushPendingInput();
      },
    }),
    []
  );

  const filteredProjectOptions = filterProjectsByStatus(projectOptions, projectStatusFilter);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isFormDisabled ? alpha(SEARCE_COLORS.neutral_200, 0.5) : SEARCE_COLORS.cardBackground,
      transition: 'background-color 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isFormDisabled ? SEARCE_COLORS.border : SEARCE_COLORS.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: SEARCE_COLORS.primary,
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: SEARCE_COLORS.primary,
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Important Information Section */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${SEARCE_COLORS.border}`,
            borderRadius: 2,
            backgroundColor: SEARCE_COLORS.cardBackground,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: SEARCE_COLORS.textPrimary,
              mb: 1,
              textDecoration: 'underline',
            }}
          >
            IMPORTANT
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              Fields marked with red asterisks(*) are mandatory fields and requires an user input
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              Sign-off can only be initiated after project status is marked as <strong>Sign Off</strong> on SFDC
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              Wait for approximately 4 hours after closing the project on SFDC to raise a sign-off request
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              {`In To, use chips (Enter to add) or paste lists — names in "Name <email>" form are parsed automatically; only addresses are sent`}
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              Files for closure report should be either in .pdf, .docx or in .doc format
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              File Name for Closure Report should be: Domain Name | Project Closure Report
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Project Selection Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
          }}
        >
          Project Selection
        </Typography>
      </Grid>

      {/* Project Code - Dynamic Dropdown */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Filter by project status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {['All', 'Closed', 'In progress', 'Yet to Start'].map((status) => {
              const isAll = status === 'All';
              const isSelected = isAll ? !projectStatusFilter : projectStatusFilter === status;
              return (
                <Chip
                  key={status}
                  label={status}
                  size="small"
                  onClick={() => onProjectStatusFilterChange(isAll ? null : (projectStatusFilter === status ? null : status))}
                  sx={{
                    backgroundColor: isSelected ? alpha(SEARCE_COLORS.primary, 0.15) : undefined,
                    color: isSelected ? SEARCE_COLORS.primary : undefined,
                    borderColor: isSelected ? SEARCE_COLORS.primary : SEARCE_COLORS.border,
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <Autocomplete
          options={filteredProjectOptions}
          getOptionLabel={(option) => option.display_label}
          value={selectedProject}
          onChange={(_, newValue) => onProjectSelect(newValue)}
          loading={projectsLoading}
          isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Code"
              required
              helperText="Select from your projects"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {projectsLoading ? <InlineSpinner size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const statusColor = getProjectStatusColor(option.status);
            const statusLabel = option.status || 'Unknown';
            return (
              <li {...props} key={option.project_code}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.project_code}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: statusColor, fontWeight: 500 }}>
                          {statusLabel}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {option.project_name} {option.account ? `• ${option.account}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }}
          noOptionsText={projectsLoading ? "Loading projects..." : "No projects found where you are the owner"}
        />
      </Grid>

      {/* Business Unit - Auto-filled from selected project */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Unit *"
          value={formData.businessUnit}
          disabled
          helperText="Auto-filled from selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Account Name - Auto-filled from selected project */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Account Name"
          value={formData.accountName}
          disabled
          helperText="Auto-filled from selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Opportunity"
          value={formData.opportunity}
          disabled
          helperText="Auto-filled from selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Opportunity / Project Owner - Auto-filled from selected project */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Opportunity / Project Owner"
          value={formData.opportunityProjectOwner}
          disabled
          helperText="Auto-filled from selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Email Sending Section */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
            mb: 2,
          }}
        >
          Send Email to Customer
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Project Title"
          required
          value={formData.projectTitle}
          onChange={(e) => onInputChange('projectTitle', e.target.value)}
          placeholder="Project Title"
          inputProps={{ maxLength: 50 }}
          helperText={`Max 50 Characters | Remaining: ${50 - formData.projectTitle.length}`}
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Customer Contact Name"
          required
          value={formData.customerContactName}
          onChange={(e) => onInputChange('customerContactName', e.target.value)}
          placeholder="Customer Contact Name"
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: SEARCE_COLORS.textPrimary, mb: 1 }}
        >
          Closure Report <span style={{ color: SEARCE_COLORS.error }}>*</span>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            sx={{
              borderColor: SEARCE_COLORS.border,
              color: SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
              },
            }}
          >
            Choose file
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={onFileChange}
            />
          </Button>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            {formData.closureReport ? formData.closureReport.name : 'No file chosen'}
          </Typography>
        </Box>
        <FormHelperText sx={{ mt: 1 }}>
          Files should be in .pdf, .docx or .doc format. File name should be: Domain Name | Project Closure Report
        </FormHelperText>
      </Grid>

      <Grid item xs={12}>
        <EmailRecipientsChipField
          ref={toEmailsFieldRef}
          label="TO:"
          required
          value={formData.toEmails}
          onChange={(next) => onInputChange('toEmails', next)}
          disabled={isFormDisabled}
          helperText="Press Enter after each address, or paste multiple lines or comma-separated lists. Display names are stripped; only the email is stored."
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <EmailRecipientsChipField
          ref={ccEmailsFieldRef}
          label="CC:"
          value={formData.ccEmails}
          onChange={(next) => onInputChange('ccEmails', next)}
          disabled={isFormDisabled}
          helperText="Optional. Same as To: chips, paste, and validation. Display names are stripped; only the email is stored."
          sx={textFieldSx}
        />
      </Grid>
    </Grid>
  );
  }
);
